/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

// eslint-disable-next-line import/no-extraneous-dependencies

import { add } from '@ckeditor/ckeditor5-utils/src/translation-service';
import { type ColorOption, Editor, ViewDowncastWriter } from 'ckeditor5';
import { CKE_TRANSLATIONS } from '../plugins/bulb-translations';
import { MEDIA } from './services/undo-redo.service';

export class EditorUtil {
  /**
   * Add translation into ckeditor translation service based user selected language.
   */
  static addTranslations($translate, defaultConfig) {
    const translations = $translate.getTranslations(defaultConfig.language);
    if (Object.prototype.hasOwnProperty.call(CKE_TRANSLATIONS, defaultConfig.language)) {
      Object.assign(translations, CKE_TRANSLATIONS[defaultConfig.language]);
    }
    add(defaultConfig.language, translations);
  }

  /**
   * Restricting Default keyboard operation for media element.
   *
   * @param editor Editor
   * @param event Event on editor.
   * @param data Data on event occur.
   */
  static restrictCopyCutEvent(editor, event, data) {
    const block = editor.model.document.selection.getSelectedElement() || editor.model.document.selection;
    if (block.name) {
      editor.bulb.service.editorDialogService.showCopyAlert(editor.t, event, data);
    } else if (block.getFirstRange()) {
      const range = block.getFirstRange();
      for (const item of range.getItems()) {
        if (item && MEDIA.includes(item.name)) {
          editor.bulb.service.editorDialogService.showCopyAlert(editor.t, event, data);
          break;
        }
      }
    }
  }

  /**
   * Enable 'read-only' state for cke.
   *
   * @param editor Current editor instance.
   * @param isCommentsAllowed Check if comment allowed for page.
   *
   */
  static enableReadOnly(editor : Editor, isCommentsAllowed : boolean) {
    const captions:any = document.querySelectorAll('figcaption');
    captions.forEach((caption) => {
      caption.setAttribute('contenteditable', false);
    });

    // Now, disable the commands on the page.
    const commands = Array.from(editor.commands.names()).map((name) => ({
      command: editor.commands.get(name),
      name,
    }));
    // Go through all of the commands and disable all of them except for commenting.
    commands.forEach((commandObj) => {
      if (!isCommentsAllowed || commandObj.name !== 'addCommentThread') {
        commandObj.command.forceDisabled('bulbReadOnly');
      }
    });
  }

  /**
   * Attach the inspector to the editor instance.
   *
   * @param editor
   *
   */
  static async attachEditor(editor) {
    const CKEditorInspector = await import('@ckeditor/ckeditor5-inspector');
    CKEditorInspector.default.attach(editor);
  }

  /**
   * Scroll to the newly inserted paragraph once asset is uploaded.
   *
   * @param editor editor instance.
   * @param tableElement contains table block.
   */
  static scrollAfterAssetInsertion(editor : any, tableElement : any) {
    if (tableElement && !tableElement.nextSibling) {
      EditorUtil.insertParagraphAfterAsset(editor, tableElement);
    } else {
      const block = editor.model.document.selection.getSelectedElement();
      const lastPosition = editor.model.document.selection.getLastPosition();
      const { nodeAfter } = lastPosition;
      if ((block && !nodeAfter) || (block && editor.bulb.service.ckeStateService.isMultipleImage())) {
        EditorUtil.insertParagraphAfterAsset(editor, block);
        if (editor.bulb.service.ckeStateService.isMultipleImage()) {
          editor.bulb.service.ckeStateService.setMultipleImage(false);
        }
      } else {
        const editingView = editor.editing.view;
        editingView.focus();
        editor.editing.view.scrollToTheSelection();
      }
    }
  }

  /**
   * Insert paragraph after asset insertion.
   *
   * @param editor editor instance.
   * @param block contains block information.
   */
  // TODO CG Replace any type:
  static insertParagraphAfterAsset(editor : any, block : any) {
    editor.execute('insertParagraph', {
      position: editor.model.createPositionAt(block, 'after'),
    });
    const editingView = editor.editing.view;
    editingView.focus();
    editor.editing.view.scrollToTheSelection();
    editor.bulb.service.ckeStateService.insertParagraphSubject.next(false);
    editor.bulb.service.ckeStateService.insertParagraphAfterTableSubject.next(null);
  }

  /**
   * Hide and show Application header and cke toolbar.
   * @param displayType Style of display.
   */
  static updateHeaderVisibility(displayType) {
    const headerElement: any = document.getElementsByClassName('bulb-header')[0];
    const ckeToolbar: any = document.getElementsByClassName('ck-editor__top')[0];
    if (headerElement && ckeToolbar) {
      ckeToolbar.style.display = displayType;
      headerElement.style.display = displayType;
    }
  }

  static cleanedUpUnwantedTags(content) {
    if (content !== '') {
      Array.from(content.getChildren()).forEach((child: any) => {
        if (child.childCount > 0) {
          EditorUtil.cleanedUpUnwantedTags(child);
        }
        /* eslint-disable no-underscore-dangle */
        if (child._attrs.keys()) {
          Array.from(child._attrs.keys()).forEach((key: any) => {
            const allowedKey = ['blockIndent', 'alignment', 'linkHref', 'italic', 'bold',
              'underline', 'bulb-id', 'listIndent', 'listItemId', 'listStyle', 'listType', 'bulb-block-id'];
            if ((allowedKey.indexOf(key) === -1)) {
              child._removeAttribute(key);
            }
          });
        }
      });
    }
    return content;
  }

  /**
   * Unselect asset and hide caption when clicked outside the cke editor.
   * @param event mouseup event
   */
  static handleClickOutsideEditor(event) {
    if (!event.target.closest('.bulb-page-content')) {
      const selectedWidget = document.getElementsByClassName('ck-widget_selected')[0];
      if (selectedWidget) {
        // Remove the selection.
        selectedWidget.classList.remove('ck-widget_selected');
      } else {
        EditorUtil.setCommentToolbarStatus(this, false);
      }
      Array.from(document.getElementsByClassName('ck-placeholder')).forEach((element) => {
        if (!element.classList.contains('ck-hidden')) {
          // Hide the caption placeholder.
          element.classList.add('ck-hidden');
        }
      });
    }
  }

  /**
   * Dynamically change comment toolbar status for enable/visible.
   *
   * @param editor editor data.
   * @param isEnabled boolean value to disable/enable comment toolbar icon.
   */
  static setCommentToolbarStatus(editor, isEnabled) {
    const toolbarItems = editor.config.get('toolbar.items');
    const index = toolbarItems.findIndex((item) => item === 'bulbComment');
    editor.ui.view.toolbar.items.get(index).isEnabled = isEnabled;
  }

  /**
   * Dynamically change comment toolbar status for hide/show.
   *
   * @param editor editor data.
   * @param isVisible boolean value to hide/show comment toolbar icon.
   */
  static setCommentToolbarVisibility(editor, isVisible) {
    const toolbarItems = editor.config.get('toolbar.items');
    const index = toolbarItems.findIndex((item) => item === 'bulbComment');
    editor.ui.view.toolbar.items.get(index).isVisible = isVisible;
  }

  /**
   * Dynamically show/hide the balloon comment button in preview mode.
   *
   * @param editor editor data.
   */
  static handleCommentBalloonVisibility(editor) {
    const { selection } = editor.model.document;
    const range = selection.getFirstRange();
    const { toolbarView } = editor.plugins.get('BalloonToolbar');
    // As of now we only have comment button in our Balloon editor therefore using "get(0)."
    const commentBalloon = toolbarView?.items.get(0);
    if (commentBalloon) {
      commentBalloon.isEnabled = !this.isTextInsideTable(range) && !editor.bulb.page.unit.viewOnly;
    }
  }

  /**
   * Check if selected text exist inside the table or not.
   *
   * @param range selection range data.
   * @return boolean true if selected text is inside the table else false.
   */
  static isTextInsideTable(range) {
    const parent = range.start?.parent?.parent;
    // Disable comment button if the selected text is inside the tableCell.
    return (parent && parent.name === 'tableCell');
  }

  /**
    * Toggle comment toolbar status for enable/visible on selection.
    *
    * @param editor editor data.
    */
  static toggleCommentToolbarBtn(editor) {
    const block = editor.model.document.selection.getSelectedElement();
    if (block) {
      if (block._attrs.get('bulb-block-id')) {
        this.setCommentToolbarStatus(editor, true);
      }
    } else {
      const { selection } = editor.model.document;
      const range = selection.getFirstRange();
      // Do nothing and disable comment button if selected text is inside a table.
      if (this.isTextInsideTable(range)) {
        return;
      }

      const selectedContent = editor.model.getSelectedContent(selection)._children._nodes;
      // Need to filter the other items so that comment button should be disabled when any other item is selected with list.
      const filterSelectedContent = selectedContent.filter((node) => node.name !== 'listItem');
      let textSelection = true;
      let itemCount = 0;

      // This will check if multiple list items are selected or not and also check there should be no other
      // items in between list items.
      if (selectedContent.length > 1 && filterSelectedContent.length === 0) {
        this.setCommentToolbarStatus(editor, true);
      } else {
        for (const item of range.getItems()) {
          // Check first selected item is text to enable comment toolbar icon
          if (itemCount === 0 && item.data) {
            this.setCommentToolbarStatus(editor, true);
          }

          // set flag if any selected range item contains block
          if (!item.data) {
            textSelection = false;
          }
          itemCount += 1;
        }
      }

      // if selected range contains block then hide comment toolbar icon.
      if (!textSelection) {
        this.setCommentToolbarStatus(editor, false);
      }
    }
  }

  /**
   * Updates the labels of color objects with their translated values based on the specified language.
   * If a translation is not available, it falls back to the original label.
   *
   * @param colors An array of objects, each containing a `color` and a `label`.
   *                 The `label` will be updated to the translated value if available.
   * @param lang A string representing the language code (e.g., 'en', 'pt') for translation.
   *
   * @returns void
   */
  static updateColorLabels(colors: Array<ColorOption>, lang: string): void {
    colors.forEach((color: ColorOption) => {
      if (typeof color !== 'string') {
        const translatedLabel = CKE_TRANSLATIONS[lang][color.label];
        color.label = translatedLabel || color.label; // Use translated label or fallback to original label
      }
    });
  }

  /**
   * Adjusts the width of `figure` elements with an inline `width` style based on the viewport size.
   *
   * This method ensures that images or other media within `figure` elements remain responsive on smaller screens.
   * It dynamically adjusts the width of `figure` elements as a percentage of the maximum allowed width (`maxAllowedWidth`)
   * on smaller screens, and restores the original pixel width on larger screens.
   * The method checks if the width is already in percentage to avoid redundant adjustments.
   *
   * @param {Editor} editor - The CKEditor instance used to map DOM elements to CKEditor view elements and apply style changes.
   */
  static adjustWidthForSmallDevices(editor: Editor) {
    const elements = editor.editing.view.document.getRoot().getChildren();
    for (const viewElement of elements) {
      if (viewElement.is('element') && viewElement.name === 'figure') {
        const width = viewElement.getStyle('width');
        if (width) {
          // Check if the width is already in percentage (ends with '%')
          if (width.endsWith('%')) {
            return;
          }
          // Numeric value of the width
          const originalWidth = parseInt(width, 10);
          this.updateWidth(originalWidth, editor, viewElement);
        }
      }
    }
  }

  /**
   * Updates the width of a CKEditor view element based on the current viewport width.
   * The width is adjusted either as a percentage or in pixels depending on whether the
   * viewport is smaller or larger than a maximum allowed width.
   *
   * @param {number} originalWidth - The original width of the element in pixels.
   * @param {Editor} editor - The CKEditor instance used to modify the editor view.
   * @param {any} viewElement - The CKEditor view element whose width needs to be updated.
   *
   */
  private static updateWidth(originalWidth: number, editor: Editor, viewElement: any) {
    const maxAllowedWidth = 815;
    const viewportWidth = window.innerWidth;

    if (viewportWidth < maxAllowedWidth) {
      // For smaller viewports, calculate the width as a percentage relative to maxAllowedWidth
      const proportionalWidth = (originalWidth / maxAllowedWidth) * 100;
      editor.editing.view.change((writer: ViewDowncastWriter) => {
        // Apply the width as a percentage to the CKEditor view element
        writer.setStyle('width', `${proportionalWidth}%`, viewElement);
      });
    } else {
      // For larger viewports, set the width to the original width in pixels
      editor.editing.view.change((writer: ViewDowncastWriter) => {
        // Apply the width in pixels to the CKEditor view element
        writer.setStyle('width', `${originalWidth}px`, viewElement);
      });
    }
  }

  /**
   * Delegates CKEditor paste handling to the dialog service,
   * prompting the user to embed pasted URLs.
   *
   * @param editor - The CKEditor instance handling the paste event.
   * @param event - The paste event object from CKEditor’s editing view document.
   * @param data - The clipboard data object containing the pasted content.
   */
  static handlePaste(editor, event, data) {
    editor.bulb.service.editorDialogService.showURLPasteDialog(editor, event, data);
  }
}
